import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
/* SLD: hard-block product link clicks inside Horizon cart drawer (capture-phase) */
(() => {
  const DRAWER_ROOTS = ['.drawer', '.cart-drawer', '[data-cart-drawer]', '.sliding-cart', 'cart-drawer'];

  const isInDrawer = (el) => DRAWER_ROOTS.some(sel => el && el.closest && el.closest(sel));
  const isProductLink = (a) => !!a && /\/products\//.test((a.getAttribute('href') || ''));

  // Click + keyboard intercept, runs before theme handlers
  const clickBlocker = (e) => {
    const a = e.target && e.target.closest && e.target.closest('a');
    if (a && isInDrawer(a) && isProductLink(a)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      a.removeAttribute('href'); // visually de-link
      a.style.pointerEvents = 'none';
      a.style.cursor = 'default';
      a.style.textDecoration = 'none';
      a.style.color = 'inherit';
    }
  };
  const keyBlocker = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const a = e.target && e.target.closest && e.target.closest('a');
    if (a && isInDrawer(a) && isProductLink(a)) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  };

  document.addEventListener('click', clickBlocker, true);
  document.addEventListener('keydown', keyBlocker, true);

  // Re-apply de-link styling after common cart events and DOM mutations
  const delink = () => {
    DRAWER_ROOTS.forEach(sel => {
      document.querySelectorAll(`${sel} a[href*="/products/"]`).forEach(a => {
        a.removeAttribute('href');
        a.style.pointerEvents = 'none';
        a.style.cursor = 'default';
        a.style.textDecoration = 'none';
        a.style.color = 'inherit';
      });
    });
  };
  ['cart:open','cart:updated','cart:refresh','ajaxProduct:added','shopify:section:load'].forEach(evt =>
    document.addEventListener(evt, delink)
  );

  const mo = new MutationObserver(delink);
  mo.observe(document.body, { childList: true, subtree: true });
  delink();
})();
